import React, { useContext, useCallback } from 'react';
import {
  Typography,
  Tag,
  BaseIcon,
  Spinner,
  Icon,
  SelectMenu,
  TextArea,
  Button,
} from '@scale/scaleui';
import { useWorkerCommentsStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useWorkerCommentsStore';
import { StepCard } from 'react-scale/components/scalers/tasks/ThreadedInteraction/step/StepCard';
import API from '@scale/frontend-lib/dashboard-api';
import { useQuery } from 'react-query';
import useDebouncedValue from 'react-scale/hooks/useDebouncedValue';
import { FeedbackResponseType, ReviewerCopilotResult } from '@scale/shared/types/chat';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-toastify';
import { useAllResponsesForStepType } from '../../Chat/hooks/ChatStore.hooks';
import { InteractionStepType } from '@scale/shared/types/chat/params';

interface ReviewerCopilotBoxProps {}

const ReviewerCopilotFeedbackSchema = z
  .object({
    feedbackResponse: z.nativeEnum(FeedbackResponseType),
    errorCategory: z.string().optional(),
    otherFeedback: z.string().optional(),
  })
  .refine(data => {
    if (data.feedbackResponse === FeedbackResponseType.Dismiss) {
      if (data.errorCategory === 'Other') {
        return data.otherFeedback !== undefined;
      }
      return data.errorCategory !== undefined;
    }
    return true;
  });

export type ReviewerCopilotFeedback = z.infer<typeof ReviewerCopilotFeedbackSchema>;

enum FeedbackErrorCategory {
  IncorrectVerdict = 'Incorrect Verdict',
  IncorrectReasoning = 'Incorrect Reasoning',
  ReasoningTooWordy = 'Reasoning too wordy',
  IncorrectLinks = 'Incorrect Links',
  TooManyLinks = 'Too many links',
  IncorrectClaimHighlighted = 'Incorrect Claim Highlighted',
  Other = 'Other',
}

const ReviewerCopilotResultFeedback: React.FC<{
  inferenceId: string;
  reviewerCopilotResult: ReviewerCopilotResult;
  hasFeedback: boolean;
  setFeedback: (feedback: ReviewerCopilotFeedback) => void;
}> = ({ inferenceId, reviewerCopilotResult, hasFeedback, setFeedback }) => {
  const { handleSubmit, watch, setValue } = useForm<ReviewerCopilotFeedback>({
    mode: 'onBlur',
    resolver: zodResolver(ReviewerCopilotFeedbackSchema),
  });

  const feedbackResponse = watch('feedbackResponse');
  const errorCategory = watch('errorCategory');
  const otherFeedback = watch('otherFeedback');

  const [loading, setLoading] = React.useState(false);

  const onSubmit = useCallback(
    async (data: ReviewerCopilotFeedback) => {
      if (loading) return;
      setLoading(true);
      try {
        await API.genai.sendReviewerCopilotFeedback({
          inferenceId,
          claimId: reviewerCopilotResult.id,
          result: data.feedbackResponse,
          errorCategory: data.errorCategory,
          otherFeedback: data.otherFeedback,
        });
        setFeedback(data);
      } catch (e) {
        toast.error(e.message);
      }
      setLoading(false);
    },
    [loading, setLoading, inferenceId, reviewerCopilotResult.id, setFeedback],
  );

  if (loading) {
    return (
      <div className="mt-2">
        <Spinner size="sm" />
      </div>
    );
  }

  if (hasFeedback) {
    return (
      <div className="mt-2">
        <Typography.Paragraph2 className="font-medium">
          Thank you for your feedback!
        </Typography.Paragraph2>
      </div>
    );
  }

  return (
    <div className="mt-2">
      <Typography.Paragraph2 className="font-medium">
        Was this helpful/accurate?
      </Typography.Paragraph2>
      <div className="flex gap-2">
        <Icon
          icon={[feedbackResponse === FeedbackResponseType.Accept ? 'fas' : 'far', 'thumbs-up']}
          size="sm"
          onClick={() => {
            setValue('feedbackResponse', FeedbackResponseType.Accept);
            handleSubmit(onSubmit)();
          }}
        />
        <Icon
          icon={[feedbackResponse === FeedbackResponseType.Dismiss ? 'fas' : 'far', 'thumbs-down']}
          size="sm"
          onClick={() => setValue('feedbackResponse', FeedbackResponseType.Dismiss)}
        />
      </div>
      {feedbackResponse === FeedbackResponseType.Dismiss && (
        <div className="mt-2">
          <SelectMenu
            label={`Select Error Category`}
            options={Object.keys(FeedbackErrorCategory).map(key => ({
              id: key,
              name: FeedbackErrorCategory[key as keyof typeof FeedbackErrorCategory],
            }))}
            selectedOptionId={errorCategory || ''}
            setSelected={selectedOption => {
              setValue('errorCategory', selectedOption);
              if (selectedOption !== FeedbackErrorCategory.Other) {
                handleSubmit(onSubmit)();
              }
            }}
          />
          {errorCategory === FeedbackErrorCategory.Other && (
            <div className="mt-2">
              <Typography.Paragraph2 className="font-medium">Other Feedback:</Typography.Paragraph2>
              <TextArea
                value={otherFeedback}
                onChange={e => setValue('otherFeedback', e.target.value)}
                rows={2}
              />
              <Button variant="primary" size="sm" onClick={handleSubmit(onSubmit)} className="mt-2">
                Submit
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const ReviewerCopilotResultViewer: React.FC<{
  inferenceId: string;
  reviewerCopilotResult: ReviewerCopilotResult;
  hasFeedback: boolean;
  setFeedback: (feedback: ReviewerCopilotFeedback) => void;
}> = ({ inferenceId, reviewerCopilotResult, hasFeedback, setFeedback }) => {
  const { claim, verdict, reasoning, links } = reviewerCopilotResult;
  const getStatusChip = (verdict: string) => {
    const lowerCaseVerdict = verdict.toLowerCase();
    if (lowerCaseVerdict.includes('true')) {
      return <Tag variant="success">True</Tag>;
    }
    if (lowerCaseVerdict.includes('false')) {
      return <Tag variant="danger">False</Tag>;
    }
    return <Tag variant="neutral">{verdict}</Tag>;
  };

  return (
    <div>
      {claim && (
        <div className="mb-2">
          <Typography.Paragraph2>
            <span className="font-medium">Claim:</span> {claim}
          </Typography.Paragraph2>
        </div>
      )}
      <div className="flex flex-row gap-2 items-center mb-2">
        <Typography.Paragraph2 className="font-medium">Validation Verdict:</Typography.Paragraph2>
        {getStatusChip(verdict)}
      </div>
      <div className="mb-2">
        <Typography.Paragraph2>
          <span className="font-medium">Reasoning:</span> {reasoning}
        </Typography.Paragraph2>
      </div>
      {links?.map((link, index) => (
        <a
          key={index}
          className="flex gap-1 items-center text-primary-400"
          target="_blank"
          rel="noopener noreferrer"
          href={link}
        >
          {new URL(link).hostname}
          <BaseIcon
            icon={['fas', 'external-link-alt']}
            contrastText
            edge="end"
            variant="secondary"
          />
        </a>
      ))}
      <ReviewerCopilotResultFeedback
        key={reviewerCopilotResult.id}
        inferenceId={inferenceId}
        reviewerCopilotResult={reviewerCopilotResult}
        hasFeedback={hasFeedback}
        setFeedback={setFeedback}
      />
    </div>
  );
};

const ReviewerCopilotBox: React.FC<ReviewerCopilotBoxProps> = () => {
  const [enabled, setEnabled] = React.useState(false);
  const selection = useWorkerCommentsStore(state => state.selection);
  const queryText = useDebouncedValue(selection.text, 1000);
  const [feedbackPerResult, setFeedbackPerResult] = React.useState<
    Record<string, ReviewerCopilotFeedback>
  >({});
  const promptInputResponses = useAllResponsesForStepType(InteractionStepType.PromptInput);
  const multiturnContinueResponses = useAllResponsesForStepType(
    InteractionStepType.MultiTurnContinue,
  );
  const referenceTexts = [...promptInputResponses, ...multiturnContinueResponses].flatMap(
    response => response.context.referenceTexts || [],
  );
  const taskContext = useContext(ChatTaskContext);
  const [claimIndex, setClaimIndex] = React.useState(0);
  const { data, error, isLoading } = useQuery({
    queryFn: async () => {
      if (!queryText || queryText.length <= 15) {
        return Promise.resolve({ results: [], inferenceId: '' });
      }
      return API.genai.fetchReviewerCopilotInference({
        content: queryText,
        taskId: taskContext.taskId,
        projectId: taskContext.projectId,
        assignmentId: taskContext.assignmentId,
        referenceTexts,
      });
    },
    queryKey: [queryText],
    staleTime: Infinity,
    enabled,
  });

  React.useEffect(() => {
    setClaimIndex(0);
    setFeedbackPerResult({});
  }, [queryText]);

  const handlePrevious = () => {
    if (claimIndex > 0) {
      setClaimIndex(claimIndex - 1);
    }
  };

  const handleNext = () => {
    if (data?.results && data.results.length && claimIndex < data.results.length - 1) {
      setClaimIndex(claimIndex + 1);
    }
  };

  if (error) return null;

  return (
    <>
      {enabled ? (
        <StepCard className="w-80">
          <StepCard.Header className="px-3 border-b border-neutral-200">
            <StepCard.HeaderLeft>
              <StepCard.Subtitle>Fact Checker</StepCard.Subtitle>
            </StepCard.HeaderLeft>
            {data?.results && data.results.length > 1 && (
              <StepCard.HeaderRight className="gap-1 flex flex-row justify-start">
                <StepCard.Button
                  icon="chevron-left"
                  onClick={handlePrevious}
                  disabled={claimIndex === 0}
                />
                <StepCard.Button
                  icon="chevron-right"
                  onClick={handleNext}
                  disabled={claimIndex === data.results.length - 1}
                />
              </StepCard.HeaderRight>
            )}
          </StepCard.Header>
          <StepCard.Body className="border-t-0 mb-1">
            {isLoading && <Spinner size="sm" />}
            {data?.results &&
              data.results.length > 0 &&
              claimIndex < data.results.length &&
              data.inferenceId && (
                <ReviewerCopilotResultViewer
                  inferenceId={data.inferenceId}
                  reviewerCopilotResult={data.results[claimIndex]}
                  hasFeedback={!!feedbackPerResult[data.results[claimIndex].id]}
                  setFeedback={feedback => {
                    setFeedbackPerResult(oldFeedback => ({
                      ...oldFeedback,
                      [data.results[claimIndex].id]: feedback,
                    }));
                  }}
                />
              )}
          </StepCard.Body>
        </StepCard>
      ) : (
        <Icon
          icon="magnifying-glass"
          size="md"
          hasBackground
          className="text-primary-400"
          onClick={() => setEnabled(true)}
          tooltip="Run fact-checking on the selected text."
        />
      )}
    </>
  );
};

export default ReviewerCopilotBox;
